import type { Role } from "@prismicio/editor-fields"
import { dateFromISOString } from "@prismicio/editor-support/Date"
import { localStorage } from "@prismicio/editor-support/DOM"
import { delay } from "@prismicio/editor-support/Promise"
import { useStableCallback } from "@prismicio/editor-support/React"
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  Image,
  ListItem,
  Separator,
  Text,
  UnorderedList,
} from "@prismicio/editor-ui"
import { type PropsWithChildren, useLayoutEffect, useState } from "react"
import { type TypeOf, z } from "zod"

import { whatsNewDialog, whatsNewGifUrl } from "../../../app/constants"
import { getRepositoryName } from "../../../app/routing"
import { useRepository } from "../../../app/services/repository"
import { trackWhatsNewModalOpen } from "../../../app/services/tracking"
import { useRole } from "../../../app/services/user"
import { useSettingsMenu } from "../../DocumentHeader/SettingsMenuProvider"
import { useDocument } from "../../DocumentProvider"

export interface WhatsNewDialogDialogProps {
  dialog: WhatsNewDialogAPI
}

export function WhatsNewDialog(props: WhatsNewDialogDialogProps) {
  const { dialog } = props

  function onOpenChange(open: boolean) {
    if (!open) dialog.close()
  }

  return (
    <Dialog
      open={dialog.isOpen}
      onOpenChange={onOpenChange}
      size={{ height: "auto", width: 560 }}
    >
      <DialogHeader
        title="You are missing the configuration of live editing"
        icon="alert"
      />

      <DialogContent>
        <Box overflowY="auto" flexDirection="column" padding={32}>
          <Text variant="h5" color="purple9">
            Introducing the live editing experience
          </Text>

          <DialogDescription asChild>
            <Text variant="h3" sx={{ marginTop: 4 }}>
              A visual editing experience with real time feedback 🎉
            </Text>
          </DialogDescription>

          <Image src={whatsNewGifUrl} sx={{ marginTop: 16 }} borderRadius={8} />

          <GetLiveEditingButton onCloseRequest={dialog.close} />

          <Separator style="dashed" sx={{ marginTop: 24, marginBottom: 8 }} />

          <SectionTitle>What is live editing?</SectionTitle>
          <Text>
            Live editing lets you see your content changes instantly, as you're
            editing - no more tab switching or full-page previews needed.
          </Text>

          <SectionTitle>Benefits of live editing:</SectionTitle>
          <UnorderedList sx={{ marginLeft: 8 }}>
            <BenefitListItem
              title="Instant Feedback"
              text="Instantly see how your text changes look in real-time without the need to switch tabs or preview the entire page."
            />
            <BenefitListItem
              title="Enhanced Accuracy"
              text="Fine-tune your content with precision, as the preview ensures that what you see is what you get."
            />
            <BenefitListItem
              title="Streamlined Workflow"
              text="Simplify your editing process by having a visual guide right beside your text editor, reducing the time spent on revisions."
            />
            <BenefitListItem
              title="Improved User Experience"
              text="Enjoy a more intuitive and responsive editing experience that keeps up with your workflow."
            />
          </UnorderedList>

          <Separator style="dashed" sx={{ marginTop: 24, marginBottom: 4 }} />

          <GetLiveEditingButton onCloseRequest={dialog.close} />
        </Box>
      </DialogContent>
    </Dialog>
  )
}

function SectionTitle(props: PropsWithChildren) {
  return (
    <Text variant="h4" sx={{ marginTop: 16, marginBottom: 8 }}>
      {props.children}
    </Text>
  )
}

interface BenefitListItemProps {
  title: string
  text: string
}

function BenefitListItem(props: BenefitListItemProps) {
  const { title, text } = props

  return (
    <ListItem>
      <Text variant="emphasized" color="purple9">
        {title}:
      </Text>{" "}
      <Text>{text}</Text>
    </ListItem>
  )
}

interface GetLiveEditingButtonProps {
  onCloseRequest: () => void
}

function GetLiveEditingButton(props: GetLiveEditingButtonProps) {
  const { onCloseRequest } = props

  const settingsMenu = useSettingsMenu()

  async function onClick() {
    onCloseRequest()
    // Wait till the Dialog is completely gone before opening the other one.
    await delay(220)
    settingsMenu.openDialog("livePreview")
  }

  return (
    <Button
      sx={{ marginTop: 20, flexShrink: 0 }}
      endIcon="arrowForward"
      size="large"
      onClick={onClick}
    >
      Get live editing
    </Button>
  )
}

interface WhatsNewDialogAPI {
  isOpen: boolean
  open: () => void
  close: () => void
}

export function useWhatsNewDialog(): WhatsNewDialogAPI {
  const [isOpen, setIsOpen] = useState(false)
  const { documentId, customType, locale } = useDocument()
  const { simulatorUrl, isSupportedFramework } = useRepository()
  const hasSimulatorUrl = simulatorUrl !== undefined

  const open = useStableCallback(() => {
    setIsOpen(true)

    const storageDialog = readLocalStorageDialog()

    const newStorageDialog: StorageDialog = {
      seenCount: storageDialog.seenCount + 1,
      lastSeen: new Date(),
    }

    localStorage.write(whatsNewDialog, JSON.stringify(newStorageDialog))

    trackWhatsNewModalOpen({
      customTypeId: customType.id,
      documentId: documentId ?? "",
      repositoryId: getRepositoryName(),
    })
  })

  function close() {
    setIsOpen(false)
  }

  const { role } = useRole(locale)

  useLayoutEffect(() => {
    if (showDialogOnStartup(hasSimulatorUrl, role, isSupportedFramework)) open()
  }, [hasSimulatorUrl, open, role, isSupportedFramework])

  return {
    isOpen,
    open,
    close,
  }
}

const storageDialog = z.object({
  lastSeen: z.string().transform(dateFromISOString),
  seenCount: z.number(),
})

type StorageDialog = TypeOf<typeof storageDialog>

function showDialogOnStartup(
  hasSimulatorURL: boolean,
  role: Role,
  isSupportedFramework: boolean,
) {
  const url = new URL(location.href)
  const forceShow = url.searchParams.has("whatsnew")

  // It's quite hard/impossible to see this Dialog once you have a valid Simulator URL or slices.
  // Provide a back door to see the Dialog again.
  if (forceShow) return true

  // Don't show if framework is not supported by slice machine
  if (!isSupportedFramework) return false

  // Don't show the dialog to Contributors
  if (role === "Contributor") return false

  // No need to show the Dialog if the URL is already good.
  if (hasSimulatorURL) return false

  // If we can't determine whether to show the Dialog, let's not annoy the user.
  if (!localStorage.isAvailable()) return false

  const dialog = readLocalStorageDialog()

  // The user has already seen the Dialog enough times.
  if (dialog.seenCount >= 3) return false

  // This user has never seen the Dialog before.
  if (!dialog.lastSeen) return true

  const now = new Date()
  const diff = now.getTime() - dialog.lastSeen.getTime()
  const dayDiff = diff / (1000 * 3600 * 24)

  // All the conditions are met to show the dialog but only do so if it's been 3 days since the last time.
  return dayDiff >= 3
}

function readLocalStorageDialog(): StorageDialog {
  const defaultValue = {
    seenCount: 0,
    lastSeen: undefined,
  }

  return localStorage.readJSON(whatsNewDialog, storageDialog) ?? defaultValue
}
